import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { PurchaseTable } from '@/components/purchases/purchase-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { useOrgId } from '@/lib/org';
import { usePurchaseTransactions, type PurchaseRow } from '@/lib/purchases';
import { cn } from '@/lib/utils';

type Chip = 'open' | 'closed' | 'all';
const CHIPS: Array<[Chip, string]> = [
  ['open', 'Open'],
  ['closed', 'Closed'],
  ['all', 'All'],
];

export const Route = createFileRoute('/expenses/purchase-orders/')({
  component: PurchaseOrders,
  validateSearch: (s: Record<string, unknown>): { status?: Chip } =>
    s['status'] === 'closed' || s['status'] === 'all' ? { status: s['status'] as Chip } : {},
});

/** Purchase orders list with status chips (document 06 E10). */
function PurchaseOrders() {
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const { status = 'open' } = Route.useSearch() as { status?: Chip };
  const [q, setQ] = useState('');
  const tx = usePurchaseTransactions();
  const needle = q.trim().toLowerCase();
  const rows = (tx.data ?? []).filter(
    (r: PurchaseRow) =>
      r.type === 'purchase_order' &&
      (status === 'all' || r.status === status) &&
      (!needle ||
        (r.contactName ?? '').toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  const actions = me.data?.isStaff ? (
    <Button asChild>
      <Link to="/expenses/purchase-orders/new">New purchase order</Link>
    </Button>
  ) : null;
  return (
    <>
      <PageHeader
        title="Purchase orders"
        description="What you have ordered from vendors. Bills receive them; nothing is posted until the bill."
        actions={actions}
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <>
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            role="tablist"
            aria-label="Status"
          >
            {CHIPS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={status === k}
                onClick={() =>
                  void navigate({
                    to: '/expenses/purchase-orders',
                    search: k === 'open' ? {} : { status: k },
                  })
                }
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  status === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
              </button>
            ))}
            <div className="relative ml-auto w-[340px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card pl-8"
                placeholder="Search by vendor, PO no. or memo"
                aria-label="Search purchase orders"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : tx.isError ? (
            <EmptyState title="Could not load purchase orders" hint={tx.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={status === 'open' ? 'No open purchase orders' : 'No purchase orders here'}
              action={actions ?? undefined}
            />
          ) : (
            <PurchaseTable rows={rows} showType={false} />
          )}
        </>
      )}
    </>
  );
}
